import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import GuestBanner from "@/components/GuestBanner";
import { api } from "@/lib/api";
import EloBadge from "@/components/EloBadge";
import { playingSince } from "@/lib/playingSince";
import PlayerLink from "@/components/PlayerLink";
import FriendsCard from "@/components/FriendsCard";
import DeleteAccount from "@/components/DeleteAccount";
import ChangeUsername from "@/components/ChangeUsername";
import Avatar2D from "@/components/Avatar2D";
import ProfileAwards from "@/components/ProfileAwards";
import { myCharacter } from "@/lib/avatarConfig";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const LAYOUT_LABEL = {
  classic: "Old Classic",
  gauntlet: "Gauntlet",
  open_field: "Open Field",
  fortress: "Classic Fortress",
  mini: "Mini",
  boardgame: "Original",
  boardgame2: "Fortress",
  fortress2: "Fortress 2.0",
  infinity: "Infinity (old)",
  infinity2: "Infinity",
  boardgame3: "Classic",
  boardgame4: "Firefly (old)",
  firefly2: "Firefly",
  boardgame5: "The Wave",
  bridges: "Bridges (old)",
  bridges_trial: "Bridges (short)",
  bridges2: "Bridges",
  bridges3: "Bridges 2.0",
};

export default function Profile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [awards, setAwards] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [h, m] = await Promise.all([api.get("/me/elo-history"), api.get("/me/matches?limit=30")]);
        setHistory(h.data);
        setMatches(m.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    api.get(`/players/${user.id}/awards`).then(({ data }) => setAwards(data.awards)).catch(() => {});
  }, [user?.id]);

  const winRate = user?.matches_played
    ? Math.round(((user.wins || 0) / user.matches_played) * 100)
    : 0;

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="profile-page">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="md:col-span-1 bg-surface border border-border rounded-2xl p-8">
          <div className="-mx-8 -mt-8 mb-6 rounded-t-2xl overflow-hidden bg-gradient-to-b from-brand/10 to-transparent relative">
            <Avatar2D character={myCharacter(user)} pose="idle" className="h-56 mx-auto pt-4 aspect-[240/256]" />
            <button
              type="button"
              onClick={() => navigate("/avatar")}
              className="absolute bottom-3 right-3 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-brand text-brand-foreground hover:bg-brand-hover"
              data-testid="customise-avatar"
            >
              Change raider
            </button>
          </div>
          <ProfileAwards awards={awards} />
          <h1 className="font-display text-3xl font-black tracking-tight break-words">{user?.username}</h1>
          <div className="mt-1"><ChangeUsername /></div>
          <p className="text-sm text-muted-foreground mt-1 truncate">{user?.email}</p>
          <div className="mt-5">
            <EloBadge elo={user?.elo_rating || 1200} size="lg" />
            <p className="mt-3 text-sm text-muted-foreground" data-testid="profile-record">
              {user?.matches_played || 0} matches · {user?.wins || 0}W / {user?.losses || 0}L
            </p>
            {playingSince(user) && (
              <p className="mt-1 text-sm text-muted-foreground" data-testid="playing-since">
                Playing since {playingSince(user)}
              </p>
            )}
          </div>
        </div>

        <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Matches", value: user?.matches_played || 0 },
            { label: "Wins", value: user?.wins || 0 },
            { label: "Losses", value: user?.losses || 0, note: user?.draws ? `${user?.draws} draw${user?.draws === 1 ? "" : "s"}` : null },
            { label: "Win rate", value: `${winRate}%` },
          ].map((s) => (
            <div key={s.label} className="bg-surface border border-border rounded-2xl p-5">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{s.label}</div>
              <div className="font-display text-3xl font-black mt-2">{s.value}</div>
              {s.note && <div className="text-xs text-muted-foreground mt-1">{s.note}</div>}
            </div>
          ))}

          <div className="col-span-2 md:col-span-4 bg-surface border border-border rounded-2xl p-6">
            <h3 className="font-display text-xl font-bold tracking-tight mb-3">Elo progression</h3>
            {history.length <= 1 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                Play matches to see your Elo curve.
              </p>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={history} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="match" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} domain={["auto", "auto"]} />
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--background))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 12,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="elo"
                      stroke="hsl(var(--brand))"
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: "hsl(var(--brand))" }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </div>

      <GuestBanner />
      {!user?.is_guest && <FriendsCard />}

      {/* Match history */}
      <div className="bg-surface border border-border rounded-2xl p-6 overflow-x-auto">
        <h3 className="font-display text-xl font-bold tracking-tight mb-4">Match history</h3>
        {loading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : matches.length === 0 ? (
          <p className="text-muted-foreground text-sm">No matches yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground text-xs uppercase tracking-[0.15em]">
                <th className="font-bold py-2">Opponent</th>
                <th className="font-bold py-2">Mode</th>
                <th className="font-bold py-2">Layout</th>
                <th className="font-bold py-2">Result</th>
                <th className="font-bold py-2">Date</th>
                <th className="font-bold py-2"></th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m) => {
                const isP1 = m.player1_id === user?.id;
                const opp = isP1 ? m.player2_username : m.player1_username;
                const me = isP1 ? "player1" : "player2";
                // Same as the public profile: winner_id decides. `result` is
                // saved from player 1's side by the live game, so flipping it
                // by result_reporter showed player 1's wins as losses.
                let result;
                if (m.win_type === "draw" || (m.result === "draw" && !m.winner_id)) {
                  result = "draw";
                } else if (m.winner_id) {
                  result = m.winner_id === user?.id ? "win" : "loss";
                } else if (m.winner_color === "A" || m.winner_color === "B") {
                  result = (m.winner_color === "A") === isP1 ? "win" : "loss";
                } else {
                  result = m.result_reporter === user?.id ? m.result
                    : m.result === "win" ? "loss" : m.result === "loss" ? "win" : m.result;
                }
                const start = m[`${me}_elo`], after = m[`${me}_new_elo`];
                const eloChange = !m.is_bot && typeof start === "number" && typeof after === "number"
                  ? after - start : null;
                const dateStr = m.completed_at || m.created_at;
                return (
                  <tr key={m.match_id} className="border-t border-border" data-testid={`history-row-${m.match_id}`}>
                    <td className="py-3 font-medium">{m.is_bot ? opp : <PlayerLink id={isP1 ? m.player2_id : m.player1_id}>{opp}</PlayerLink>}</td>
                    <td className="py-3 capitalize text-muted-foreground">{m.mode?.replace("_", "-")}</td>
                    <td className="py-3 capitalize text-muted-foreground">
                      {m.layout ? LAYOUT_LABEL[m.layout] : "—"}
                    </td>
                    <td className="py-3 whitespace-nowrap">
                      {m.status === "completed" ? (
                        <span
                          className={`text-xs uppercase tracking-wider font-bold px-2.5 py-1 rounded-full ${
                            result === "win"
                              ? "bg-[hsl(var(--chart-4))]/20 text-[hsl(var(--chart-4))]"
                              : result === "loss"
                              ? "bg-destructive/15 text-destructive"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {result}
                        </span>
                      ) : (
                        <span className="text-xs uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-brand/15 text-brand">
                          active
                        </span>
                      )}
                      {m.status === "completed" && eloChange != null && eloChange !== 0 && (
                        <span className="ml-2 text-xs font-semibold text-muted-foreground" data-testid={`history-elo-${m.match_id}`}>
                          {eloChange > 0 ? `+${eloChange}` : eloChange}
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-muted-foreground text-xs">
                      {dateStr ? new Date(dateStr).toLocaleString() : "—"}
                    </td>
                    <td className="py-3 text-right">
                      {m.has_moves ? (
                        <button
                          onClick={() => navigate(`/watch/${m.match_id}`, { state: { back: true } })}
                          className="text-xs font-bold uppercase tracking-wider text-brand hover:underline"
                          data-testid={`step-through-${m.match_id}`}
                        >
                          Step through
                        </button>
                      ) : m.has_replay ? (
                        <button
                          onClick={() => navigate(`/replay/${m.match_id}`)}
                          className="text-xs font-bold uppercase tracking-wider text-brand hover:underline"
                          data-testid={`watch-replay-${m.match_id}`}
                        >
                          Watch replay
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!user?.is_guest && <DeleteAccount />}
    </main>
  );
}
